import React, { useState, useEffect, useContext } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import Dropdown from '../../Components/utils/Dropdown';
import tradeLeadImage from '../../images/pngs/tradeLead.png';
import { CategoryContext } from '../../Contexts/CategoryContext';
import { LoginContext } from '../../Contexts/LoginContext';
import Loader from '../../Components/utils/Loader';
import { showToast } from '../../Components/utils/ShowToast';
import { today, tommorrow } from "../../data/constants"
import { FaInfoCircle } from 'react-icons/fa';

const TradeLeadForm = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { categories } = useContext(CategoryContext);
  const { user } = useContext(LoginContext);
  const editData = location.state?.editData;

  const [formData, setFormData] = useState({
    product: '',
    category: '',
    subcategory: '',
    childcategory: '',
    sellingScope: 'local',
    state: '',
    city: '',
    location: '',
    requirementDetails: '',
    budget: '',
    quantity: '',
    neededDate: '',
    neededTime: '',
    expiryDate: '',
    deliveryOption: 'Pick up',
    event: '',
    kilometersAway: '',
  });
  const [isLoading, setIsLoading] = useState(false);

  // Check if buyer has address set
  const hasAddress = user?.buyerAddress?.country?.name;

  useEffect(() => {
    if (editData) {
      setFormData({
        product: editData.product || '',
        category: editData.category || '',
        subcategory: editData.subcategory || '',
        childcategory: editData.childcategory || '',
        sellingScope: editData.sellingScope || 'local',
        state: editData.state || '',
        city: editData.city || '',
        location: editData.location || '',
        requirementDetails: editData.requirementDetails || '',
        budget: editData.budget || '',
        quantity: editData.quantity || '',
        neededDate: editData.neededDate ? editData.neededDate.split('T')[0] : '',
        neededTime: editData.neededTime || '',
        expiryDate: editData.expiryDate ? editData.expiryDate.split('T')[0] : '',
        deliveryOption: editData.deliveryOption || 'Pick up',
        event: editData.event || '',
        kilometersAway: editData.kilometersAway || '',
      });
    }
  }, [editData]);

  // Determine category scope
  const selectedCat = categories.find(cat => cat.name === formData.category);
  const categoryScope = selectedCat?.scope || 'local';

  // When category changes, auto-set sellingScope based on scope
  const handleCategoryChange = (selected) => {
    const cat = categories.find(c => c.name === selected?.value);
    setFormData(prev => ({
      ...prev,
      category: selected?.value || '',
      subcategory: '',
      childcategory: '',
      sellingScope: cat?.scope === 'local' ? 'local' : prev.sellingScope,
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!hasAddress) {
      showToast('Please set your address in Settings before posting a requirement', 'warning');
      return;
    }

    // Validation checks
    const todayDate = new Date();
    const neededDate = new Date(formData.neededDate);
    const expiryDate = new Date(formData.expiryDate);
    const todayStart = new Date(todayDate.setHours(0, 0, 0, 0));

    if (formData.budget <= 0) {
      showToast('Budget must be greater than 0', 'error');
      return;
    }

    if (formData.quantity < 1) {
      showToast('Quantity must be at least 1', 'error');
      return;
    }

    if (neededDate < todayStart) {
      showToast('Needed date must be today or later', 'error');
      return;
    }

    if (neededDate.toDateString() === todayDate.toDateString()) {
      if (!formData.neededTime) {
        showToast('Needed time is required if date is today', 'error');
        return;
      }
      const [hours, minutes] = formData.neededTime.split(':').map(Number);
      const neededTime = new Date();
      neededTime.setHours(hours, minutes, 0, 0);
      if (neededTime <= todayDate) {
        showToast('Needed time must be later than current time', 'error');
        return;
      }
    }

    const tomorrowStart = new Date(todayStart);
    tomorrowStart.setDate(tomorrowStart.getDate() + 1);
    if (expiryDate < tomorrowStart) {
      showToast('Expiry date must be at least one day later than today', 'error');
      return;
    }

    // Local scope validation
    if (formData.sellingScope === 'local') {
      if (!formData.city || !formData.state) {
        showToast('Please fill in city and state for local requirements', 'error');
        return;
      }
    }

    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      // Build payload - don't send country for local (backend auto-fills from profile)
      const payload = { ...formData };
      if (payload.sellingScope === 'international') {
        // International: clear preferred area fields
        delete payload.state;
        delete payload.city;
        delete payload.location;
        delete payload.kilometersAway;
      }
      // Country is always auto-filled by backend from profile

      if (editData) {
        await axios.put(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/tradeleads/${editData._id}`, payload, config);
        showToast('Tradelead updated successfully', 'success');
      } else {
        await axios.post(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/tradeleads`, payload, config);
        showToast('Tradelead posted successfully', 'success');
      }
      navigate('/postRequirements');
    } catch (error) {
      showToast(error.response?.data?.message || 'Error submitting tradelead', 'error');
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const categoryOptions = categories.map(cat => ({ value: cat.name, label: cat.name }));
  const subcategoryOptions = formData.category
    ? categories.find(cat => cat.name === formData.category)?.subcategories.map(sub => ({ value: sub.name, label: sub.name })) || []
    : [];
  const selectedSub = selectedCat?.subcategories?.find(sub => sub.name === formData.subcategory);
  const childcategoryOptions = selectedSub?.children?.map(child => ({ value: child.name, label: child.name })) || [];
  const kilometerOptions = [
    { value: '<10', label: '< 10 km' },
    { value: '10-25', label: '10-25 km' },
    { value: '25-50', label: '25-50 km' },
    { value: '50-100', label: '50-100 km' },
    { value: '100-500', label: '100-500 km' },
    { value: '500+', label: '500+ km' },
  ];

  const isInternational = formData.sellingScope === 'international';

  // No address warning
  if (!hasAddress) {
    return (
      <div className="tradeLeadPage">
        <section className="mainSection">
          <div className="tradeLeadFormHeader">
            <nav className="tradeLeadBreadcrumb">
              <Link to="/">Home</Link>
              <span className="breadcrumbSep">/</span>
              <Link to="/postRequirements">Post Your Requirements</Link>
              <span className="breadcrumbSep">/</span>
              <span className="breadcrumbCurrent">Post Requirement</span>
            </nav>
          </div>
          <h1 className="pageMainHeading">Tell us your Requirement</h1>
          <div className="cpInfoBanner" style={{ maxWidth: '600px', margin: '40px auto' }}>
            <FaInfoCircle />
            <div>
              <p style={{ fontWeight: 600, marginBottom: '8px' }}>Address Required</p>
              <p>You need to set your address before posting a requirement. Sellers need to know your location to provide accurate quotes and delivery estimates.</p>
              <button
                type="button"
                className="primaryBtn"
                style={{ marginTop: '16px' }}
                onClick={() => navigate('/settings')}
              >
                Go to Settings
              </button>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="tradeLeadPage">
      {isLoading && <Loader type="checkmate" />}
      <section className="mainSection">
        <div className="tradeLeadFormHeader">
          <nav className="tradeLeadBreadcrumb">
            <Link to="/">Home</Link>
            <span className="breadcrumbSep">/</span>
            <Link to="/postRequirements">Post Your Requirements</Link>
            <span className="breadcrumbSep">/</span>
            <span className="breadcrumbCurrent">
              {editData ? 'Edit Trade Lead' : 'Post Requirement'}
            </span>
          </nav>
        </div>
        <h1 className="pageMainHeading">{editData ? 'Edit Trade Lead' : 'Tell us your Requirement'}</h1>
        <div className="tradeLeadContainer">
          <div className="tradeLeadImage">
            <img src={tradeLeadImage} alt="Trade Lead" />
          </div>
          <div className="tradeLeadFormContainer">
            <form onSubmit={handleSubmit}>
              <div className="tradeLeadGroup">
                <h2 className="tradeLeadSectionLabel">Product Details</h2>
                <div className="tradeLeadField">
                  <label htmlFor="product">Product</label>
                  <input
                    id="product"
                    type="text"
                    className="inputField"
                    placeholder="Product you are looking for"
                    name="product"
                    value={formData.product}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="tradeLeadRow">
                  <div className="tradeLeadField">
                    <label htmlFor="category">Category</label>
                    <Dropdown
                      id="category"
                      options={categoryOptions}
                      value={formData.category ? { value: formData.category, label: formData.category } : null}
                      onChange={handleCategoryChange}
                      placeholder="Select Category"
                      isClearable
                      required
                    />
                  </div>
                  <div className="tradeLeadField">
                    <label htmlFor="subcategory">Sub-Category</label>
                    <Dropdown
                      id="subcategory"
                      options={subcategoryOptions}
                      value={formData.subcategory ? { value: formData.subcategory, label: formData.subcategory } : null}
                      onChange={(selected) => setFormData(prev => ({ ...prev, subcategory: selected?.value || '', childcategory: '' }))}
                      placeholder="Select Sub-Category"
                      isClearable
                      disabled={!formData.category}
                      required
                    />
                  </div>
                  {childcategoryOptions.length > 0 && (
                    <div className="tradeLeadField">
                      <label htmlFor="childcategory">Child Category</label>
                      <Dropdown
                        id="childcategory"
                        options={childcategoryOptions}
                        value={formData.childcategory ? { value: formData.childcategory, label: formData.childcategory } : null}
                        onChange={(selected) => setFormData(prev => ({ ...prev, childcategory: selected?.value || '' }))}
                        placeholder="Select Child Category"
                        isClearable
                      />
                    </div>
                  )}
                  <div className="tradeLeadField">
                    <label htmlFor="event">Event</label>
                    <input
                      id="event"
                      type="text"
                      className="inputField"
                      placeholder="E.g; Birthday"
                      name="event"
                      value={formData.event}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                {/* Scope selection — follows CreateProduct pattern */}
                {selectedCat && categoryScope === 'local' && (
                  <div className="cpInfoBanner" style={{ marginTop: '12px' }}>
                    <FaInfoCircle />
                    <p>Products/services in this category are only available locally (within your country).</p>
                  </div>
                )}
                {selectedCat && categoryScope === 'international' && (
                  <div className="tradeLeadField" style={{ marginTop: '12px' }}>
                    <label>Buying Scope</label>
                    <Dropdown
                      options={[
                        { value: 'local', label: 'Buy Locally (within your country)' },
                        { value: 'international', label: 'Buy Internationally (worldwide)' },
                      ]}
                      value={{
                        value: formData.sellingScope,
                        label: formData.sellingScope === 'local' ? 'Buy Locally' : 'Buy Internationally'
                      }}
                      onChange={(selected) => setFormData(prev => ({ ...prev, sellingScope: selected.value }))}
                      placeholder="Select buying scope"
                    />
                  </div>
                )}
              </div>

              {/* Preferred Area — only for local scope */}
              {!isInternational && (
                <div className="tradeLeadGroup">
                  <h2 className="tradeLeadSectionLabel">Preferred Area</h2>
                  <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '12px' }}>
                    Your country ({user?.buyerAddress?.country?.name}) is automatically used from your profile.
                  </p>
                  <div className="tradeLeadRow">
                    <div className="tradeLeadField">
                      <label htmlFor="state">State</label>
                      <input
                        id="state"
                        type="text"
                        className="inputField"
                        placeholder="State"
                        name="state"
                        value={formData.state}
                        required
                        onChange={handleChange}
                      />
                    </div>
                    <div className="tradeLeadField">
                      <label htmlFor="city">City</label>
                      <input
                        id="city"
                        type="text"
                        className="inputField"
                        placeholder="City"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>
                  <div className="tradeLeadRow">
                    <div className="tradeLeadField">
                      <label htmlFor="location">Location/Area</label>
                      <input
                        id="location"
                        type="text"
                        className="inputField"
                        placeholder="Location/Area"
                        name="location"
                        value={formData.location}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="tradeLeadField">
                      <label htmlFor="kilometersAway">Preferred Range</label>
                      <Dropdown
                        id="kilometersAway"
                        options={kilometerOptions}
                        value={formData.kilometersAway ? { value: formData.kilometersAway, label: formData.kilometersAway } : null}
                        onChange={(selected) => setFormData(prev => ({ ...prev, kilometersAway: selected?.value || '' }))}
                        placeholder="How far Vendor can be?"
                        isClearable
                        required
                      />
                    </div>
                  </div>
                </div>
              )}

              <div className="tradeLeadGroup">
                <h2 className="tradeLeadSectionLabel">Requirement Details</h2>
                <div className="tradeLeadField">
                  <label htmlFor="requirementDetails">Requirements</label>
                  <textarea
                    id="requirementDetails"
                    className="inputField"
                    name="requirementDetails"
                    value={formData.requirementDetails}
                    onChange={handleChange}
                    placeholder="Enter your requirements"
                    required
                  />
                </div>
                <div className="tradeLeadRow">
                  <div className="tradeLeadField">
                    <label htmlFor="budget">Targeted Budget ($)</label>
                    <input
                      id="budget"
                      type="number"
                      className="inputField"
                      placeholder="Enter Targeted Budget in USD"
                      name="budget"
                      value={formData.budget}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="tradeLeadField">
                    <label htmlFor="quantity">Quantity</label>
                    <input
                      id="quantity"
                      type="number"
                      className="inputField"
                      placeholder="Enter Quantity"
                      name="quantity"
                      value={formData.quantity}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="tradeLeadGroup">
                <h2 className="tradeLeadSectionLabel">Date & Time</h2>
                <div className="tradeLeadRow">
                  <div className="tradeLeadField">
                    <label htmlFor="neededDate">Date Needed</label>
                    <input
                      id="neededDate"
                      type="date"
                      className="inputField"
                      name="neededDate"
                      value={formData.neededDate}
                      onChange={handleChange}
                      required
                      min={today}
                    />
                  </div>
                  <div className="tradeLeadField">
                    <label htmlFor="neededTime">Time Needed</label>
                    <input
                      id="neededTime"
                      type="time"
                      className="inputField"
                      name="neededTime"
                      value={formData.neededTime}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>
                <div className="tradeLeadRow">
                  <div className="tradeLeadField">
                    <label htmlFor="expiryDate">Tradelead/Request Expiry Date</label>
                    <input
                      id="expiryDate"
                      type="date"
                      className="inputField"
                      name="expiryDate"
                      value={formData.expiryDate}
                      onChange={handleChange}
                      min={tommorrow}
                      required
                    />
                  </div>
                </div>
                <p className="expiryNote">Note: After this date, the posting will be automatically deleted.</p>
              </div>

              <div className="tradeLeadGroup">
                <h2 className="tradeLeadSectionLabel">How will you receive?</h2>
                <div className="deliveryOptions">
                  <label>
                    <input
                      type="radio"
                      name="deliveryOption"
                      value="Pick up"
                      checked={formData.deliveryOption === 'Pick up'}
                      onChange={handleChange}
                    /> Pick Up
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="deliveryOption"
                      value="Home Delivery"
                      checked={formData.deliveryOption === 'Home Delivery'}
                      onChange={handleChange}
                    /> Home Delivery
                  </label>
                </div>
              </div>

              <div className="tradeLeadRow">
                <button type="submit" className="primaryBtn" disabled={isLoading}>
                  {editData ? 'Update' : 'Confirm'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TradeLeadForm;